import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useApp } from '../../context/AppContext';

function useMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const PAGE_META = {
  'dashboard': { label: 'Overview' },
  'approvals': { label: 'Approvals' },
  'admin-customers': { label: 'Customers' },
  'admin-vessels': { label: 'Vessels' },
  'admin-suppliers': { label: 'Suppliers' },
  'admin-masters': { label: 'Configurations' },
  'new-entry': { label: 'New Stock Entry' },
  'stock-list': { label: 'Stock List' },
  'delivery': { label: 'Delivery Orders' },
  'my-stock': { label: 'Stock List' },
  'my-deliveries': { label: 'Delivery Orders' },
  'profile': { label: 'My Profile Settings' },
};

const USERS = {
  admin: { name: 'Ahmad Malik', initials: 'AM', grad: 'linear-gradient(135deg,#4A7FB5,#1E3A5F)' },
  warehouse: { name: 'Budi Wibowo', initials: 'BW', grad: 'linear-gradient(135deg,#E8A838,#d4922a)' },
  customer: { name: 'Ahmad Rizki', initials: 'AR', grad: 'linear-gradient(135deg,#6BA3D6,#4A7FB5)' },
};

const ROLES = {
  admin: 'Administrator',
  warehouse: 'Warehouse Staff',
  customer: 'Customer',
};

export default function Topbar({ current, onNav, onLogout, loggedUser: _propUser, onMenuToggle }) {
  const { role, pendingStock, loggedUser } = useApp();
  const meta = PAGE_META[current] || { label: current };
  const isMobile = useMobile();

  // Measure env(safe-area-inset-top) as a real pixel value once on mount.
  // iOS Safari can return 0 for env() during rapid JS re-renders (e.g. right after login).
  // useLayoutEffect runs before the browser paints, so the state update + re-render
  // completes before the first paint — no visible flash.
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const d = document.createElement('div');
      d.style.cssText = 'position:fixed;top:0;left:0;height:env(safe-area-inset-top,0px);width:0;min-height:0;pointer-events:none;visibility:hidden;';
      document.documentElement.appendChild(d);
      const h = parseFloat(getComputedStyle(d).height) || 0;
      d.remove();
      return h;
    };
    const h = measure();
    if (h > 0) { setSafePaddingTop(h); return; }
    // iOS may need a frame to resolve env() on first SPA navigation
    const t1 = setTimeout(() => { const v = measure(); if (v > 0) setSafePaddingTop(v); }, 50);
    const t2 = setTimeout(() => { const v = measure(); if (v > 0) setSafePaddingTop(v); }, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Use global loggedUser from context as the primary source of truth
  const displayName = loggedUser?.name || _propUser?.name || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const dropRef = useRef(null);
  const notifRef = useRef(null);

  /* Close dropdowns on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const notifications = role === 'admin' ? [
    ...(pendingStock.length > 0 ? [{ id: 1, text: `${pendingStock.length} items pending approval`, icon: '📦', time: 'Just now', target: 'approvals' }] : []),
    { id: 2, text: 'DN ALN2601001 dispatched', icon: '🚚', time: '2h ago', target: 'delivery' },
    { id: 3, text: 'New stock entry submitted', icon: '📋', time: '4h ago', target: 'stock-list' },
  ] : [];

  const handleNotifClick = (target) => {
    setShowNotif(false);
    onNav(target);
  };

  /* Theme management */
  const [theme, setTheme] = useState(() => localStorage.getItem('cargoflow-theme') || 'light');
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  const applyTheme = (mode) => {
    setTheme(mode);
    localStorage.setItem('cargoflow-theme', mode);
    setShowThemeMenu(false);
    if (mode === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else if (mode === 'light') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      // system
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) document.documentElement.setAttribute('data-theme', 'dark');
      else document.documentElement.removeAttribute('data-theme');
    }
  };

  // Apply theme on mount
  useEffect(() => { applyTheme(theme); }, []);

  const themeIcons = { light: '☀️', dark: '🌙' };
  const themeLabels = { light: 'Light', dark: 'Dark' };

  const menuItems = [
    { label: 'Profile', icon: '👤', onClick: () => { setShowDropdown(false); onNav('profile'); } },
    { label: `Theme: ${themeLabels[theme]}`, icon: themeIcons[theme], onClick: () => setShowThemeMenu(!showThemeMenu), hasSubmenu: true },
    'divider',
    { label: 'Logout', icon: '⏻', onClick: () => { setShowDropdown(false); onLogout && onLogout(); }, danger: true },
  ];

  return (
    <div className="topbar-root" style={{
      minHeight: 60, background: 'var(--white)', borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', paddingLeft: 16, paddingRight: 16, gap: 10, flexShrink: 0,
      ...(safePaddingTop > 0 ? { paddingTop: safePaddingTop } : {})
    }}>
      {/* Hamburger — mobile only */}
      {isMobile && (
        <button onClick={onMenuToggle} style={{
          width: 38, height: 38, borderRadius: 9, border: '1px solid var(--border)',
          background: 'var(--surface2)', cursor: 'pointer', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0
        }}>
          <span style={{ width: 16, height: 2, background: 'var(--text2)', borderRadius: 2, display: 'block' }} />
          <span style={{ width: 16, height: 2, background: 'var(--text2)', borderRadius: 2, display: 'block' }} />
          <span style={{ width: 16, height: 2, background: 'var(--text2)', borderRadius: 2, display: 'block' }} />
        </button>
      )}

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)', flex: 1 }}>
        <span className="hide-mobile">Home</span>
        <span className="hide-mobile" style={{ color: 'var(--muted2)' }}>›</span>
        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{meta.label}</span>
      </div>

      {/* Right side: Notification bell + User dropdown */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>

        {/* Notification Bell (Hide for customers) */}
        {role !== 'customer' && (
          <div ref={notifRef} style={{ position: 'relative' }}>
            <div onClick={() => { setShowNotif(!showNotif); setShowDropdown(false); }}
              style={{
                width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', fontSize: 18, position: 'relative',
                background: showNotif ? 'var(--accent-light)' : 'transparent',
                border: showNotif ? '1px solid #bfdbfe' : '1px solid transparent',
                transition: 'all .15s'
              }}
              onMouseEnter={e => { if (!showNotif) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { if (!showNotif) e.currentTarget.style.background = 'transparent'; }}>
              <svg width="18" height="18" fill="none" stroke="var(--text2)" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
              {pendingStock.length > 0 && (
                <span style={{
                  position: 'absolute', top: 4, right: 4, width: 8, height: 8, borderRadius: '50%',
                  background: 'var(--danger)', border: '2px solid var(--white)'
                }} />
              )}
            </div>

            {/* Notification Dropdown */}
            {showNotif && (
              <div style={{
                position: 'absolute', top: 44, right: 0, width: 320, background: 'var(--white)',
                border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)',
                zIndex: 999, animation: 'modalIn .15s ease', overflow: 'hidden'
              }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                  Notifications
                </div>
                {notifications.map(n => (
                  <div key={n.id} onClick={() => handleNotifClick(n.target)} style={{
                    padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start',
                    borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .1s'
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ fontSize: 16 }}>{n.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>{n.text}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{n.time}</div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginTop: 2 }}>→</span>
                  </div>
                ))}
                <div onClick={() => handleNotifClick('approvals')} style={{ padding: '8px 14px', textAlign: 'center', fontSize: 12, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>
                  View All →
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Profile */}
        <div style={{ position: 'relative' }} ref={dropRef}>
          <div onClick={() => setShowDropdown(!showDropdown)} style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            padding: '4px 8px 4px 4px', borderRadius: 24, transition: 'background .2s',
            background: showDropdown ? 'var(--surface2)' : 'transparent'
          }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
            onMouseLeave={e => { if (!showDropdown) e.currentTarget.style.background = 'transparent'; }}>

            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, letterSpacing: '1px', boxShadow: 'var(--shadow)',
              overflow: 'hidden'
            }}>
              {loggedUser?.photo ? (
                <img src={loggedUser.photo} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                initials
              )}
            </div>
            {!isMobile && (
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{displayName}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>{ROLES[role]}</div>
              </div>
            )}
            {!isMobile && (
              <svg width="12" height="12" fill="none" stroke="var(--muted)" strokeWidth="2" viewBox="0 0 24 24" style={{ marginLeft: 2 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute', top: 46, right: 0, width: 200, background: 'var(--white)',
              border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)',
              zIndex: 999, animation: 'modalIn .15s ease', overflow: 'hidden', padding: '4px 0'
            }}>
              {menuItems.map((item, i) => {
                if (item === 'divider') return <div key={i} style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />;
                return (
                  <div key={i}>
                    <div onClick={item.onClick}
                      style={{
                        padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 10,
                        cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'background .1s',
                        color: item.danger ? 'var(--danger)' : 'var(--text2)'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = item.danger ? 'var(--danger-bg)' : 'var(--surface2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: 15 }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.hasSubmenu && <span style={{ fontSize: 10, color: 'var(--muted)' }}>▾</span>}
                    </div>
                    {/* Theme submenu */}
                    {item.hasSubmenu && showThemeMenu && (
                      <div style={{ background: 'var(--surface2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                        {['light', 'dark'].map(t => (
                          <div key={t} onClick={() => applyTheme(t)}
                            style={{
                              padding: '7px 14px 7px 40px', display: 'flex', alignItems: 'center', gap: 8,
                              cursor: 'pointer', fontSize: 12.5, fontWeight: 500, transition: 'background .1s',
                              color: theme === t ? 'var(--accent)' : 'var(--text2)',
                              background: theme === t ? 'var(--accent-light)' : 'transparent'
                            }}
                            onMouseEnter={e => { if (theme !== t) e.currentTarget.style.background = 'var(--white)'; }}
                            onMouseLeave={e => { if (theme !== t) e.currentTarget.style.background = 'transparent'; }}>
                            <span style={{ fontSize: 14 }}>{themeIcons[t]}</span>
                            <span style={{ flex: 1 }}>{themeLabels[t]}</span>
                            {theme === t && <span style={{ fontSize: 12, fontWeight: 700 }}>✓</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
